import type { AstNode, CommentProvider, MaybePromise } from "langium";
import { AstNodeHoverProvider, type LangiumServices } from "langium/lsp";
import type { Hover, MarkupContent } from "vscode-languageserver";

import {
	isAlias,
	isDirective,
	isDirectiveParameter,
	isEndpoint,
	isEnum,
	isException,
	isImportMember,
	isModule,
	isScalar,
	isScalarOption,
	isStruct,
} from "../generated/ast.js";

export class IprotoHoverProvider extends AstNodeHoverProvider {
	private commentProvider: CommentProvider;

	constructor(services: LangiumServices) {
		super(services);
		this.commentProvider = services.documentation.CommentProvider;
	}

	protected override getAstNodeHoverContent(node: AstNode): MaybePromise<Hover | undefined> {
		if (isAlias(node)) {
			return this.generateHover(`alias ${node.name}`, node);
		}

		if (isScalar(node)) {
			let declaration = `scalar ${node.name}`;

			if (node.parameters.length > 0) {
				declaration += `<${node.parameters.map((param) => param.name).join(", ")}>`;
			}

			return this.generateHover(declaration, node);
		}

		if (isScalarOption(node)) {
			return this.generateHover(`${node.name}:${node.type}`, node);
		}

		if (isEnum(node)) {
			return this.generateHover(`enum ${node.name}`, node);
		}

		if (isStruct(node)) {
			return this.generateHover(`struct ${node.name}`, node);
		}

		if (isEndpoint(node)) {
			return this.generateHover(`endpoint ${node.name}`, node);
		}

		if (isDirective(node)) {
			return this.generateHover(`directive ${node.name}`, node);
		}

		if (isDirectiveParameter(node)) {
			return this.generateHover(`${node.name}${node.optional ? "?" : ""}:${node.type}`, node);
		}

		if (isModule(node)) {
			return this.generateHover(`module ${node.name}`, node);
		}

		if (isException(node)) {
			return this.generateHover(`exception ${node.name}`, node);
		}

		if (isImportMember(node)) {
			if (node.target.ref) {
				return this.getAstNodeHoverContent(node.target.ref);
			}
		}

		return this.generateHover("", node);
	}

	private generateHover(declaration: string, node: AstNode): Hover {
		const contents: MarkupContent = {
			kind: "markdown",
			value: "",
		};

		const comment = this.commentProvider.getComment(node);

		if (comment) {
			contents.value += comment.substring(2, comment.length - 2).trim();
			contents.value += "\n\n";
		}

		if (declaration) {
			contents.value += `\`\`\`iproto\n${declaration}\n\`\`\``;
		}

		return { contents };
	}
}
